import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Swords, Trophy, History, UserPlus, UserMinus } from 'lucide-react';
import { toast } from 'sonner';

const ClientPKBattle = ({ userKakoId, userNickname, userAvatar }) => {
  const [config, setConfig] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [myHistory, setMyHistory] = useState([]);
  const [currentWinners, setCurrentWinners] = useState(null);
  const [winnersHistory, setWinnersHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myParticipation, setMyParticipation] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    // Selecionar ÚLTIMA rodada automaticamente quando carregar
    if (rounds.length > 0 && !selectedRound) {
      setSelectedRound(rounds[rounds.length - 1].round_number);
    }
  }, [rounds]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([
      loadConfig(),
      loadParticipants(),
      loadRounds(),
      loadMyHistory(),
      loadCurrentWinners(),
      loadWinnersHistory()
    ]);
    setLoading(false);
  };

  const loadCurrentWinners = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/winners/current-with-data`);
      setCurrentWinners(response.data);
    } catch (error) {
      console.error('Erro ao carregar ganhadores:', error);
    }
  };

  const loadWinnersHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/winners/history`);
      setWinnersHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico de ganhadores:', error);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const loadParticipants = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/participants`);
      setParticipants(response.data);
      
      // Verificar se estou inscrito
      const myData = response.data.find(p => p.kako_id === userKakoId);
      setMyParticipation(myData || null);
    } catch (error) {
      console.error('Erro ao carregar participantes:', error);
    }
  };

  const loadRounds = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/rounds`);
      // Filtrar apenas rodadas visíveis ao público
      const visibleRounds = response.data.filter(r => r.show_to_public);
      setRounds(visibleRounds);
    } catch (error) {
      console.error('Erro ao carregar rodadas:', error);
    }
  };

  const loadMyHistory = async () => {
    if (!userKakoId) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pk-battle/history/${userKakoId}`);
      setMyHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const handleInscribe = async () => {
    if (!window.confirm('Você tem certeza que deseja se inscrever na BATALHA DE PK?')) {
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/pk-battle/inscribe`, {
        kako_id: userKakoId,
        nickname: userNickname,
        avatar_url: userAvatar
      });
      toast.success('Inscrição realizada com sucesso! 🎉');
      loadParticipants();
    } catch (error) {
      console.error('Erro ao se inscrever:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao se inscrever';
      toast.error(displayError);
    }
  };

  const handleCancelInscription = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua inscrição? Você não poderá se inscrever novamente!')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/pk-battle/inscribe/${userKakoId}`);
      toast.success('Inscrição cancelada');
      loadParticipants();
    } catch (error) {
      console.error('Erro ao cancelar:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string' ? errorMsg : 
                          Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : 
                          'Erro ao cancelar inscrição';
      toast.error(displayError);
    }
  };

  const inscritosAtivos = participants.filter(p => p.status === 'inscrito');

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  // Verificar se alguma aba está visível
  const hasVisibleTabs = config?.show_inscription_tab || config?.show_rounds_tab || config?.show_winners_tab || config?.show_rules_tab;

  if (!hasVisibleTabs) {
    return (
      <div className="text-center py-12">
        <Swords className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <p className="text-xl text-gray-500">BATALHA DE PK em breve...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card 
        className="relative overflow-hidden"
        style={{ 
          backgroundImage: 'url(/pk-background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Overlay para legibilidade */}
        <div className="absolute inset-0 bg-black/20 z-0"></div>
        
        <CardHeader className="text-center py-4 relative z-10">
          {/* Imagem do Título */}
          <img 
            src="/pk-banner.png" 
            alt="BATALHA DE PK" 
            className="max-w-full h-auto mx-auto"
            style={{ maxHeight: '200px' }}
          />
        </CardHeader>
        <CardContent className="relative z-10">
          <Tabs defaultValue={config?.show_rules_tab ? "rules" : config?.show_inscription_tab ? "inscription" : config?.show_rounds_tab ? "rounds" : "history"} className="w-full">
            <TabsList className={`grid w-full ${
              (() => {
                const count = [config?.show_rules_tab, config?.show_inscription_tab, config?.show_rounds_tab, config?.show_winners_tab, true].filter(Boolean).length;
                return `grid-cols-${count}`;
              })()
            }`}>
              {config?.show_rules_tab && (
                <TabsTrigger value="rules" className="text-xs sm:text-sm">
                  <span className="hidden sm:inline">📜 Regras</span>
                  <span className="sm:hidden">📜</span>
                </TabsTrigger>
              )}
              {config?.show_inscription_tab && (
                <TabsTrigger value="inscription" className="text-xs sm:text-sm">
                  <span className="hidden sm:inline">📝 Inscrição</span>
                  <span className="sm:hidden">📝</span>
                </TabsTrigger>
              )}
              {config?.show_rounds_tab && (
                <TabsTrigger value="rounds" className="text-xs sm:text-sm">
                  <span className="hidden sm:inline">🎮 Rodadas</span>
                  <span className="sm:hidden">🎮</span>
                </TabsTrigger>
              )}
              {config?.show_winners_tab && (
                <TabsTrigger value="winners" className="text-xs sm:text-sm">
                  <span className="hidden sm:inline">🏆 Ganhadores</span>
                  <span className="sm:hidden">🏆</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="history" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">📊 Meu Histórico</span>
                <span className="sm:hidden">📊</span>
              </TabsTrigger>
            </TabsList>

            {/* TAB: INSCRIÇÃO */}
            {config?.show_inscription_tab && (
              <TabsContent value="inscription" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">📋 Inscrições</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Status da Inscrição */}
                    <div className="p-4 bg-gray-50 rounded-lg border-2 flex items-center justify-between gap-2">
                      <p className="font-semibold">Status das Inscrições:</p>
                      {config?.inscriptions_open ? (
                        <Badge className="bg-green-600">✅ Abertas</Badge>
                      ) : (
                        <Badge className="bg-red-600">🔒 Fechadas</Badge>
                      )}
                    </div>

                    {/* Minha Participação */}
                    {myParticipation && (
                      <div className="p-4 border-2 rounded-lg flex items-center justify-between gap-2">
                        <p className="font-semibold">Meu Status:</p>
                        {myParticipation.status === 'inscrito' && (
                          <Badge className="bg-green-600">✅ Inscrito</Badge>
                        )}
                        {myParticipation.status === 'cancelado' && (
                          <Badge className="bg-orange-600">❌ Cancelado</Badge>
                        )}
                        {myParticipation.status === 'desclassificado' && (
                          <Badge className="bg-red-600">🚫 Desclassificado</Badge>
                        )}
                      </div>
                    )}

                    {/* Botões de Ação */}
                    {config?.inscriptions_open && !myParticipation && (
                      <Button onClick={handleInscribe} className="w-full" size="lg">
                        <UserPlus className="w-5 h-5 mr-2" />
                        Inscrever-se na Batalha
                      </Button>
                    )}

                    {config?.inscriptions_open && myParticipation?.status === 'inscrito' && (
                      <Button onClick={handleCancelInscription} variant="destructive" className="w-full" size="lg">
                        <UserMinus className="w-5 h-5 mr-2" />
                        Cancelar Minha Inscrição
                      </Button>
                    )}

                    {/* Lista de Inscritos */}
                    <div className="mt-6">
                      <h3 className="font-bold text-lg mb-3">
                        Participantes Inscritos ({inscritosAtivos.length}/32)
                      </h3>
                      {inscritosAtivos.length >= 32 && (
                        <div className="mb-3 p-3 bg-red-50 border-2 border-red-300 rounded-lg">
                          <p className="text-sm text-red-700 font-semibold text-center">
                            ⚠️ Limite de participantes atingido!
                          </p>
                        </div>
                      )}
                      {inscritosAtivos.length === 0 ? (
                        <p className="text-gray-500">Nenhum inscrito ainda</p>
                      ) : (
                        <div className="space-y-2">
                          {inscritosAtivos.map((p) => (
                            <div
                              key={p.kako_id}
                              className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border rounded-lg ${
                                p.kako_id === userKakoId ? 'bg-blue-50 border-blue-400' : ''
                              }`}
                            >
                              {p.avatar_url && (
                                <img src={p.avatar_url} alt={p.nickname} className="w-8 sm:w-10 h-8 sm:h-10 rounded-full flex-shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm sm:text-base truncate">{p.nickname}</p>
                                {p.kako_id === userKakoId && (
                                  <p className="text-xs text-blue-600">Você</p>
                                )}
                              </div>
                              <Badge className="bg-green-500 text-xs sm:text-sm flex-shrink-0">Inscrito</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* TAB: RODADAS */}
            {config?.show_rounds_tab && (
              <TabsContent value="rounds" className="space-y-4">
                {rounds.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nenhuma rodada disponível ainda</p>
                ) : (
                  <>
                    {/* Menu Dropdown de Rodadas */}
                    <div className="mb-4">
                      <select 
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 bg-white font-semibold text-base focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        value={selectedRound || ''}
                        onChange={(e) => setSelectedRound(parseInt(e.target.value))}
                      >
                        {rounds.map((round) => (
                          <option key={round.round_number} value={round.round_number}>
                            🎮 Rodada {round.round_number} {round.round_datetime && `- ${round.round_datetime}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Matches da Rodada Selecionada */}
                    {selectedRound && rounds.find(r => r.round_number === selectedRound)?.matches.map((match, idx, arr) => {
                              const p1Data = match.participant1_data;
                              const p2Data = match.participant2_data;
                              const byeData = match.bye_participant_data;
                              const isMyMatch = p1Data?.kako_id === userKakoId || 
                                                p2Data?.kako_id === userKakoId || 
                                                byeData?.kako_id === userKakoId;

                              // Card de BYE Compacto
                              if (match.is_bye) {
                                return (
                                  <React.Fragment key={match.match_id}>
                                    <div className={`bg-green-600 rounded-lg p-3 flex items-center gap-3 ${isMyMatch ? 'border-4 border-yellow-400' : ''}`}>
                                      {byeData?.avatar_url && (
                                        <img src={byeData.avatar_url} alt="" className="w-12 h-12 rounded-full border-2 border-white" />
                                      )}
                                      <div className="flex-1">
                                        <p className="font-bold text-white">{byeData?.nickname || 'Desconhecido'}</p>
                                        <p className="text-xs text-green-100">✅ Classificado Automaticamente (BYE)</p>
                                        {isMyMatch && <p className="text-xs text-yellow-300 font-semibold mt-1">👤 Você</p>}
                                      </div>
                                    </div>
                                    {/* Separador entre cards */}
                                    {idx < arr.length - 1 && (
                                      <div className="my-4">
                                        <img src="/pk-divider.png" alt="" className="w-full h-auto opacity-80" />
                                      </div>
                                    )}
                                  </React.Fragment>
                                );
                              }

                              // Card de Dupla Normal
                              const totalPoints = (match.points_participant1 || 0) + (match.points_participant2 || 0);
                              
                              // Calcular porcentagem com garantia de mínimo 1% se tiver pontos
                              let p1Percentage = 50;
                              let p2Percentage = 50;
                              
                              if (totalPoints > 0) {
                                const p1Raw = (match.points_participant1 / totalPoints) * 100;
                                const p2Raw = (match.points_participant2 / totalPoints) * 100;
                                
                                // Se tem pontos, garantir mínimo de 1%
                                if (match.points_participant1 > 0 && p1Raw < 1) {
                                  p1Percentage = 1;
                                  p2Percentage = 99;
                                } else if (match.points_participant2 > 0 && p2Raw < 1) {
                                  p1Percentage = 99;
                                  p2Percentage = 1;
                                } else {
                                  // Usar cálculo exato - NUNCA 50/50 se pontos são diferentes
                                  if (match.points_participant1 !== match.points_participant2) {
                                    p1Percentage = p1Raw;
                                    p2Percentage = p2Raw;
                                  } else {
                                    // Só 50/50 se pontos realmente iguais
                                    p1Percentage = 50;
                                    p2Percentage = 50;
                                  }
                                }
                              }
                              
                              const isFinalized = match.status === 'finalizado' || 
                                                  match.status === 'wo' || 
                                                  match.status === 'wo_both_disqualified' || 
                                                  match.status === 'both_advance';

                              return (
                                <React.Fragment key={match.match_id}>
                                  <div className={`border-2 rounded-lg overflow-hidden ${isMyMatch ? 'border-yellow-400 shadow-lg' : 'border-gray-300'}`}>
                                  {/* Horário e Status no topo */}
                                  <div className="bg-gray-100 py-2 px-4 text-center border-b space-y-1">
                                    {match.battle_datetime && (
                                      <p className="text-sm font-semibold text-gray-700">⏰ {match.battle_datetime}</p>
                                    )}
                                    <Badge
                                      className={
                                        match.status === 'finalizado'
                                          ? 'bg-green-600'
                                          : match.status === 'wo'
                                          ? 'bg-yellow-600'
                                          : match.status === 'wo_both_disqualified'
                                          ? 'bg-gray-600'
                                          : match.status === 'both_advance'
                                          ? 'bg-blue-600'
                                          : 'bg-gray-500'
                                      }
                                    >
                                      {match.status === 'finalizado' 
                                        ? '✅ Finalizado' 
                                        : match.status === 'wo' 
                                        ? '⚠️ WO' 
                                        : match.status === 'wo_both_disqualified'
                                        ? '🚫 WO - Ambos Desclassificados'
                                        : match.status === 'both_advance'
                                        ? '🤝 Ambos Classificados'
                                        : '⏳ Aguardando'}
                                    </Badge>
                                  </div>
                                  
                                  {/* Card dividido em duas colunas com fundos azul e vermelho */}
                                  <div className="grid grid-cols-2 relative">
                                    {/* Imagem VS no centro */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                      <img src="/vs-icon.png" alt="VS" className="w-20 h-20 drop-shadow-lg" />
                                    </div>

                                    {/* TIME AZUL - Lado Esquerdo */}
                                    <div 
                                      className="bg-blue-50 p-3 bg-cover bg-center relative"
                                      style={{ backgroundImage: 'url(/bg-blue.png)' }}
                                    >
                                      {/* Overlay para melhor legibilidade */}
                                      <div className="absolute inset-0 bg-blue-50/70"></div>
                                      
                                      <div className="relative z-10 flex flex-col items-center gap-2">
                                        {/* 
                                          ⚠️ PROPORÇÕES PERFEITAS - NÃO ALTERAR:
                                          - Container: w-32 h-32 (128px)
                                          - Avatar: w-[98px] h-[98px] (98px)
                                          - Moldura: absolute inset-0 com top: 2px
                                          - Proporção: Avatar = 76.5% do container
                                          - Centralização perfeita com flex items-center justify-center
                                        */}
                                        <div className="relative w-32 h-32 flex items-center justify-center">
                                          {/* Avatar centralizado - TAMANHO: 98px */}
                                          {p1Data?.avatar_url && (
                                            <img src={p1Data.avatar_url} alt="" className="w-[98px] h-[98px] rounded-full border-2 border-blue-400" />
                                          )}
                                          {/* Moldura 128px envolvendo avatar - DESLOCAMENTO: 2px para baixo */}
                                          {isFinalized && (match.winner_kako_id === p1Data?.kako_id || match.status === 'both_advance') && (
                                            <img src="/frame-winner.png" alt="Ganhou" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                          )}
                                          {isFinalized && match.winner_kako_id !== p1Data?.kako_id && match.status !== 'both_advance' && match.status !== 'wo_both_disqualified' && (
                                            <img src="/frame-loser.png" alt="Perdeu" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                          )}
                                        </div>
                                        <div className="text-center px-1">
                                          <p className="font-bold text-blue-700 text-sm sm:text-base truncate">{p1Data?.nickname || 'Desconhecido'}</p>
                                          {/* Badge WO - antes do ID */}
                                          {(match.status === 'wo' || match.status === 'wo_both_disqualified' || match.status === 'both_advance') && (
                                            <Badge className="bg-yellow-600 text-xs mt-1">⚠️ W.O</Badge>
                                          )}
                                          {isMyMatch && p1Data?.kako_id === userKakoId ? (
                                            <p className="text-xs text-yellow-600 font-semibold mt-1 truncate">👤 Você</p>
                                          ) : (
                                            !isMyMatch && <p className="text-xs text-gray-600 mt-1 truncate">#{p1Data?.kako_id}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* TIME VERMELHO - Lado Direito */}
                                    <div 
                                      className="bg-red-50 p-3 bg-cover bg-center relative"
                                      style={{ backgroundImage: 'url(/bg-red.png)' }}
                                    >
                                      {/* Overlay para melhor legibilidade */}
                                      <div className="absolute inset-0 bg-red-50/70"></div>
                                      
                                      <div className="relative z-10 flex flex-col items-center gap-2">
                                        {/* 
                                          ⚠️ PROPORÇÕES PERFEITAS - NÃO ALTERAR:
                                          - Container: w-32 h-32 (128px)
                                          - Avatar: w-[98px] h-[98px] (98px)
                                          - Moldura: absolute inset-0 com top: 2px
                                          - Proporção: Avatar = 76.5% do container
                                          - Centralização perfeita com flex items-center justify-center
                                        */}
                                        <div className="relative w-32 h-32 flex items-center justify-center">
                                          {/* Avatar centralizado - TAMANHO: 98px */}
                                          {p2Data?.avatar_url && (
                                            <img src={p2Data.avatar_url} alt="" className="w-[98px] h-[98px] rounded-full border-2 border-red-400" />
                                          )}
                                          {/* Moldura 128px envolvendo avatar - DESLOCAMENTO: 2px para baixo */}
                                          {isFinalized && (match.winner_kako_id === p2Data?.kako_id || match.status === 'both_advance') && (
                                            <img src="/frame-winner.png" alt="Ganhou" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                          )}
                                          {isFinalized && match.winner_kako_id !== p2Data?.kako_id && match.status !== 'both_advance' && match.status !== 'wo_both_disqualified' && (
                                            <img src="/frame-loser.png" alt="Perdeu" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                          )}
                                        </div>
                                        <div className="text-center px-1">
                                          <p className="font-bold text-red-700 text-sm sm:text-base truncate">{p2Data?.nickname || 'Desconhecido'}</p>
                                          {/* Badge WO - antes do ID */}
                                          {(match.status === 'wo' || match.status === 'wo_both_disqualified' || match.status === 'both_advance') && (
                                            <Badge className="bg-yellow-600 text-xs mt-1">⚠️ W.O</Badge>
                                          )}
                                          {isMyMatch && p2Data?.kako_id === userKakoId ? (
                                            <p className="text-xs text-yellow-600 font-semibold mt-1 truncate">👤 Você</p>
                                          ) : (
                                            !isMyMatch && <p className="text-xs text-gray-600 mt-1 truncate">#{p2Data?.kako_id}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Barra Visual - Só aparece quando finalizado NORMAL (não WO) */}
                                  {match.status === 'finalizado' && (
                                    <div className="bg-gray-100 p-2 relative">
                                      <div className="flex items-center justify-between gap-2 text-base font-bold mb-1">
                                        <span className="text-blue-600">{match.points_participant1}</span>
                                        <span className="text-red-600">{match.points_participant2}</span>
                                      </div>
                                      {/* X centralizado absolutamente */}
                                      <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
                                        <span className="text-gray-600 font-bold text-lg">×</span>
                                      </div>
                                      <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden flex">
                                        <div
                                          className="bg-blue-500 flex items-center justify-center text-white text-xs font-bold"
                                          style={{ width: `${p1Percentage}%` }}
                                        >
                                          {p1Percentage > 15 && `${Math.round(p1Percentage)}%`}
                                        </div>
                                        <div
                                          className="bg-red-500 flex items-center justify-center text-white text-xs font-bold"
                                          style={{ width: `${p2Percentage}%` }}
                                        >
                                          {p2Percentage > 15 && `${Math.round(p2Percentage)}%`}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Separador entre cards */}
                                {idx < arr.length - 1 && (
                                  <div className="my-4">
                                    <img src="/pk-divider.png" alt="" className="w-full h-auto opacity-80" />
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                  </>
                )}
              </TabsContent>
            )}

            {/* TAB: GANHADORES */}
            {config?.show_winners_tab && (
              <TabsContent value="winners" className="space-y-4">
                {!currentWinners ? (
                  <p className="text-gray-500 text-center py-8">Ganhadores ainda não foram definidos</p>
                ) : (
                  <Tabs defaultValue="current" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="current">🏆 Ganhadores Atuais</TabsTrigger>
                      <TabsTrigger value="history">📜 Edições Anteriores</TabsTrigger>
                    </TabsList>

                    {/* Ganhadores Atuais */}
                    <TabsContent value="current" className="space-y-4 mt-4">
                      {/* TOP 1 */}
                      <Card className="border-4 border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100">
                        <CardContent className="p-6 text-center space-y-3">
                          <h3 className="text-3xl font-bold text-yellow-700">🥇 CAMPEÃO</h3>
                          {/* 
                            ⚠️ PROPORÇÕES PERFEITAS CAMPEÃO - NÃO ALTERAR:
                            - Container: w-[280px] h-[280px] (280px)
                            - Avatar: w-40 h-40 (160px)
                            - Proporção: 57% (avatar/container)
                            - Espaço moldura: 120px (60px cada lado)
                            - Moldura: frame-champion.png com top: 2px
                          */}
                          <div className="relative w-[280px] h-[280px] mx-auto flex items-center justify-center">
                            {currentWinners.top1?.avatar && (
                              <img src={currentWinners.top1.avatar} alt="" className="w-40 h-40 rounded-full border-4 border-yellow-500" />
                            )}
                            <img src="/frame-champion.png" alt="Campeão" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                          </div>
                          <p className="text-2xl font-bold">{currentWinners.top1?.nickname || 'Desconhecido'}</p>
                          <p className="text-sm text-gray-600">ID: {currentWinners.top1?.kako_id}</p>
                          {currentWinners.top1?.prize && (
                            <div className="mt-3 p-3 bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-400 rounded-lg">
                              <p className="text-xs text-green-700 font-semibold">🏆 PRÊMIO</p>
                              <p className="text-2xl font-black text-green-600">{currentWinners.top1.prize}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* TOP 2 */}
                      {currentWinners.top2 && (
                        <Card className="border-4 border-gray-400 bg-gradient-to-br from-gray-50 to-gray-100">
                          <CardContent className="p-4 sm:p-6 text-center space-y-3">
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-700">🥈 VICE-CAMPEÃO</h3>
                            <div className="relative w-[200px] sm:w-[280px] h-[200px] sm:h-[280px] mx-auto flex items-center justify-center">
                              {currentWinners.top2?.avatar && (
                                <img src={currentWinners.top2.avatar} alt="" className="w-32 sm:w-40 h-32 sm:h-40 rounded-full border-4 border-gray-500" />
                              )}
                              <img src="/frame-vice.png" alt="Vice-Campeão" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                            </div>
                            <p className="text-lg sm:text-xl font-bold">{currentWinners.top2?.nickname || 'Desconhecido'}</p>
                            <p className="text-sm text-gray-600">ID: {currentWinners.top2?.kako_id}</p>
                            {currentWinners.top2?.prize && (
                              <div className="mt-3 p-3 bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-400 rounded-lg">
                                <p className="text-xs text-green-700 font-semibold">🏆 PRÊMIO</p>
                                <p className="text-xl font-black text-green-600">{currentWinners.top2.prize}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* Histórico */}
                    <TabsContent value="history" className="space-y-3 mt-4">
                      {winnersHistory.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">Nenhum evento anterior</p>
                      ) : (
                        winnersHistory.map((event, idx) => (
                          <Card key={idx} className="border-2">
                            <CardHeader className="bg-purple-50">
                              <CardTitle className="text-lg">🏆 {winnersHistory.length - idx}ª Edição - {event.event_date}</CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 space-y-3">
                              <div className="flex items-center gap-3 bg-yellow-50 p-3 rounded-lg">
                                {event.winner_top1_avatar && (
                                  <img src={event.winner_top1_avatar} alt="" className="w-16 h-16 rounded-full border-2 border-yellow-500" />
                                )}
                                <div className="flex-1">
                                  <p className="font-bold">🥇 {event.winner_top1_nickname}</p>
                                  <p className="text-sm text-yellow-700 font-semibold">1º LUGAR</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                                {event.winner_top2_avatar && (
                                  <img src={event.winner_top2_avatar} alt="" className="w-14 h-14 rounded-full border-2 border-gray-400" />
                                )}
                                <div className="flex-1">
                                  <p className="font-bold">🥈 {event.winner_top2_nickname}</p>
                                  <p className="text-sm text-gray-600 font-semibold">2º LUGAR</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </TabsContent>
                  </Tabs>
                )}
              </TabsContent>
            )}


            {/* TAB: REGRAS */}
            {config?.show_rules_tab && (
              <TabsContent value="rules" className="space-y-4">
                <Card className="border-2 border-purple-300">
                  <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                    <CardTitle className="text-2xl flex items-center gap-2">
                      📜 Regras da Batalha de PK
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Prêmios em Destaque */}
                    {(config?.prize_top1 || config?.prize_top2) && (
                      <div className="mb-6 p-3 sm:p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 border-yellow-400 rounded-lg">
                        <h3 className="text-lg sm:text-xl font-bold text-center mb-3">🏆 PREMIAÇÃO 🏆</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          {config.prize_top1 && (
                            <div className="text-center p-2 sm:p-3 bg-yellow-200 rounded-lg">
                              <p className="text-xs sm:text-sm font-semibold text-yellow-800">🥇 1º LUGAR</p>
                              <p className="text-lg sm:text-2xl font-bold text-yellow-900 break-words">{config.prize_top1}</p>
                            </div>
                          )}
                          {config.prize_top2 && (
                            <div className="text-center p-2 sm:p-3 bg-gray-200 rounded-lg">
                              <p className="text-xs sm:text-sm font-semibold text-gray-700">🥈 2º LUGAR</p>
                              <p className="text-base sm:text-xl font-bold text-gray-800 break-words">{config.prize_top2}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Regras do Evento */}
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                        {config?.rules_text || 'Regras serão divulgadas em breve...'}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* TAB: MEU HISTÓRICO */}
            <TabsContent value="history" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-6 h-6" />
                    Meu Histórico de Batalhas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myHistory.filter(m => m.result !== 'aguardando').length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Você ainda não participou de nenhuma batalha finalizada</p>
                  ) : (
                    <div className="space-y-3">
                      {myHistory.filter(m => m.result !== 'aguardando').map((match, idx) => {
                        // BYE
                        if (match.result === 'bye') {
                          return (
                            <Card key={idx} className="bg-green-50 border-green-300 overflow-hidden">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-semibold">Rodada {match.round_number}</p>
                                    <p className="text-sm text-gray-600">Classificado Automaticamente (BYE)</p>
                                  </div>
                                  <Badge className="bg-green-600">✅ Avançou</Badge>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        }

                        // Determinar dados do usuário e oponente
                        const isUserP1 = match.participant1_data?.kako_id === userKakoId;
                        const userData = isUserP1 ? match.participant1_data : match.participant2_data;
                        const opponentData = isUserP1 ? match.participant2_data : match.participant1_data;
                        const userPoints = isUserP1 ? match.points_participant1 : match.points_participant2;
                        const opponentPoints = isUserP1 ? match.points_participant2 : match.points_participant1;
                        
                        // Calcular porcentagens para barra
                        const totalPoints = userPoints + opponentPoints;
                        let userPercentage = 50;
                        let opponentPercentage = 50;
                        
                        if (totalPoints > 0) {
                          const userRaw = (userPoints / totalPoints) * 100;
                          const opponentRaw = (opponentPoints / totalPoints) * 100;
                          
                          if (userPoints > 0 && userRaw < 1) {
                            userPercentage = 1;
                            opponentPercentage = 99;
                          } else if (opponentPoints > 0 && opponentRaw < 1) {
                            userPercentage = 99;
                            opponentPercentage = 1;
                          } else {
                            // Usar cálculo exato - NUNCA 50/50 se pontos são diferentes
                            if (userPoints !== opponentPoints) {
                              userPercentage = userRaw;
                              opponentPercentage = opponentRaw;
                            } else {
                              // Só 50/50 se pontos realmente iguais
                              userPercentage = 50;
                              opponentPercentage = 50;
                            }
                          }
                        }
                        
                        const isFinalized = true; // Histórico só tem finalizadas

                        return (
                          <div key={idx} className="border-2 rounded-lg overflow-hidden border-yellow-400 shadow-lg">
                            {/* Cabeçalho: Rodada e Status */}
                            <div className="bg-gray-100 py-2 px-4 text-center border-b space-y-1">
                              <p className="text-sm font-semibold text-gray-700">🎮 Rodada {match.round_number}</p>
                              {match.battle_datetime && (
                                <p className="text-xs text-gray-600">⏰ {match.battle_datetime}</p>
                              )}
                              <Badge className={
                                match.result === 'vitoria' || match.result === 'wo_victory'
                                  ? 'bg-green-600' 
                                  : match.result === 'derrota' || match.result === 'wo_loss'
                                  ? 'bg-red-600'
                                  : match.result === 'wo_both_disqualified'
                                  ? 'bg-gray-600'
                                  : match.result === 'both_advance'
                                  ? 'bg-blue-600'
                                  : 'bg-gray-500'
                              }>
                                {match.result === 'vitoria' 
                                  ? '🏆 Vitória' 
                                  : match.result === 'wo_victory'
                                  ? '⚠️ Vitória por WO'
                                  : match.result === 'derrota'
                                  ? '❌ Derrota'
                                  : match.result === 'wo_loss'
                                  ? '🚫 Desclassificado por WO'
                                  : match.result === 'wo_both_disqualified'
                                  ? '🚫 Ambos Desclassificados'
                                  : match.result === 'both_advance'
                                  ? '🤝 Ambos Classificados'
                                  : 'Aguardando'}
                              </Badge>
                            </div>
                            
                            {/* Card dividido em duas colunas */}
                            <div className="grid grid-cols-2 relative">
                              {/* Imagem VS no centro */}
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                <img src="/vs-icon.png" alt="VS" className="w-20 h-20 drop-shadow-lg" />
                              </div>

                              {/* VOCÊ - Lado Esquerdo */}
                              <div 
                                className="bg-blue-50 p-3 bg-cover bg-center relative"
                                style={{ backgroundImage: 'url(/bg-blue.png)' }}
                              >
                                <div className="absolute inset-0 bg-blue-50/70"></div>
                                
                                <div className="relative z-10 flex flex-col items-center gap-2">
                                  <div className="relative w-32 h-32 flex items-center justify-center">
                                    {userData?.avatar_url && (
                                      <img src={userData.avatar_url} alt="" className="w-[98px] h-[98px] rounded-full border-2 border-blue-400" />
                                    )}
                                    {/* Moldura de resultado */}
                                    {(match.result === 'vitoria' || match.result === 'wo_victory' || match.result === 'both_advance') && (
                                      <img src="/frame-winner.png" alt="Ganhou" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                    )}
                                    {(match.result === 'derrota' || match.result === 'wo_loss') && (
                                      <img src="/frame-loser.png" alt="Perdeu" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                    )}
                                  </div>
                                  <div className="text-center px-1">
                                    <p className="font-bold text-blue-700 text-sm sm:text-base truncate">{userData?.nickname || 'Você'}</p>
                                    {/* Badge WO - antes do "Você" */}
                                    {(match.result === 'wo_victory' || match.result === 'wo_loss' || match.result === 'wo_both_disqualified' || match.result === 'both_advance') && (
                                      <Badge className="bg-yellow-600 text-xs mt-1">⚠️ W.O</Badge>
                                    )}
                                    <p className="text-xs text-yellow-600 font-semibold mt-1">👤 Você</p>
                                  </div>
                                </div>
                              </div>

                              {/* OPONENTE - Lado Direito */}
                              <div 
                                className="bg-red-50 p-3 bg-cover bg-center relative"
                                style={{ backgroundImage: 'url(/bg-red.png)' }}
                              >
                                <div className="absolute inset-0 bg-red-50/70"></div>
                                
                                <div className="relative z-10 flex flex-col items-center gap-2">
                                  <div className="relative w-32 h-32 flex items-center justify-center">
                                    {opponentData?.avatar_url && (
                                      <img src={opponentData.avatar_url} alt="" className="w-[98px] h-[98px] rounded-full border-2 border-red-400" />
                                    )}
                                    {/* Moldura de resultado - INVERTIDO para oponente */}
                                    {(match.result === 'derrota' || match.result === 'wo_loss' || match.result === 'both_advance') && (
                                      <img src="/frame-winner.png" alt="Ganhou" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                    )}
                                    {(match.result === 'vitoria' || match.result === 'wo_victory') && (
                                      <img src="/frame-loser.png" alt="Perdeu" className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ top: '2px' }} />
                                    )}
                                  </div>
                                  <div className="text-center px-1">
                                    <p className="font-bold text-red-700 text-sm sm:text-base truncate">{opponentData?.nickname || 'Oponente'}</p>
                                    {/* Badge WO - antes do ID */}
                                    {(match.result === 'wo_victory' || match.result === 'wo_loss' || match.result === 'wo_both_disqualified' || match.result === 'both_advance') && (
                                      <Badge className="bg-yellow-600 text-xs mt-1">⚠️ W.O</Badge>
                                    )}
                                    <p className="text-xs text-gray-600 mt-1 truncate">#{opponentData?.kako_id}</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Barra Visual - Só em vitórias/derrotas normais (não WO) */}
                            {(match.result === 'vitoria' || match.result === 'derrota') && (
                              <div className="bg-gray-100 p-2 relative">
                                <div className="flex items-center justify-between gap-2 text-base font-bold mb-1">
                                  <span className="text-blue-600">{userPoints}</span>
                                  <span className="text-red-600">{opponentPoints}</span>
                                </div>
                                {/* X centralizado absolutamente */}
                                <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
                                  <span className="text-gray-600 font-bold text-lg">×</span>
                                </div>
                                <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden flex">
                                  <div
                                    className="bg-blue-500 flex items-center justify-center text-white text-xs font-bold"
                                    style={{ width: `${userPercentage}%` }}
                                  >
                                    {userPercentage > 15 && `${Math.round(userPercentage)}%`}
                                  </div>
                                  <div
                                    className="bg-red-500 flex items-center justify-center text-white text-xs font-bold"
                                    style={{ width: `${opponentPercentage}%` }}
                                  >
                                    {opponentPercentage > 15 && `${Math.round(opponentPercentage)}%`}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientPKBattle;
